import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Card from '../components/Card';
import { getAllProducts, likeProduct } from '../services/productService';

interface WishlistItem {
  id: number;
  title: string;
  creator: string;
  price: number;
  isLiked: boolean;
  imageUrl?: string;
  rating: number;
  sales: number;
  badges: string[];
  isNew: boolean;
  discount: number;
  addedOn: string;
}

const WishlistPage: React.FC = () => {
  const [wishlistItems, setWishlistItems] = useState<WishlistItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [sortBy, setSortBy] = useState<string>('date');

  // Fetch products and filter for liked items
  useEffect(() => {
    const fetchWishlist = async () => {
      try {
        setLoading(true);
        // Get all products
        const products = await getAllProducts();

        // In a real app, you would have an endpoint to get only liked products
        // For now, we'll simulate it by filtering all products
        // Ideally, the backend would provide a /wishlist endpoint

        // Convert API products to WishlistItem format
        // In a real implementation, you would have user-specific "liked" status
        // This is simulating by taking a subset of products
        const likedProducts = products
          .filter((_, index) => index % 3 === 0) // Simulated "liked" products
          .map(product => ({
            id: product.id,
            title: product.title,
            creator: product.sellerName,
            price: product.price,
            isLiked: true, // These are all liked since they're in the wishlist
            imageUrl: product.images[0],
            rating: product.rating,
            sales: Math.floor(product.downloads * 0.7), // Simulate sales from downloads
            badges: product.tags.slice(0, 2),
            isNew: new Date(product.releaseDate) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
            discount: 0, // This would come from product data
            addedOn: new Date(Date.now() - Math.floor(Math.random() * 30) * 24 * 60 * 60 * 1000).toISOString().split('T')[0] // Random recent date
          }));

        setWishlistItems(sortWishlistItems(likedProducts, sortBy));
      } catch (error) {
        console.error('Error fetching wishlist:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchWishlist();
  }, []);

  // Helper function to sort wishlist items
  const sortWishlistItems = (items: WishlistItem[], sortType: string): WishlistItem[] => {
    const sorted = [...items];

    switch (sortType) {
      case 'price-low':
        sorted.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        sorted.sort((a, b) => b.price - a.price);
        break;
      case 'name':
        sorted.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'date':
      default:
        sorted.sort((a, b) => new Date(b.addedOn).getTime() - new Date(a.addedOn).getTime());
        break;
    }

    return sorted;
  };

  const handleRemoveItem = async (id: number) => {
    try {
      // In a real implementation, you would call an API endpoint to unlike the product
      await likeProduct(id); // Toggle the like status

      // Update the local state
      setWishlistItems(wishlistItems.filter(item => item.id !== id));
    } catch (error) {
      console.error('Error removing item from wishlist:', error);
    }
  };

  const handleClearWishlist = () => {
    // In a real implementation, you would call an API to clear the wishlist
    // For now, we'll just update the local state
    setWishlistItems([]);
  };

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSortBy = e.target.value;
    setSortBy(newSortBy);
    setWishlistItems(sortWishlistItems(wishlistItems, newSortBy));
  };

  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold">My Wishlist</h1>
        <p className="text-[var(--color-muted)] mt-2">Items you've liked and saved for later</p>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#7F56D9]"></div>
        </div>
      ) : wishlistItems.length > 0 ? (
        <>
          <div className="flex justify-between items-center mb-6">
            <p className="text-[var(--color-muted)]">{wishlistItems.length} items in your wishlist</p>
            <div className="flex gap-4 items-center">
              <div className="flex items-center gap-2">
                <label htmlFor="sort" className="text-sm text-[var(--color-muted)]">Sort by:</label>
                <select
                  id="sort"
                  className="bg-[#1F1A27] border border-white/10 rounded-md py-1 px-2 text-sm"
                  value={sortBy}
                  onChange={handleSortChange}
                >
                  <option value="date">Date Added</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="name">Name</option>
                </select>
              </div>
              <button
                className="text-sm text-[#FF3C8E] hover:underline"
                onClick={handleClearWishlist}
              >
                Clear All
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {wishlistItems.map((item) => (
              <div key={item.id} className="relative group">
                <Card
                  id={item.id}
                  title={item.title}
                  creator={item.creator}
                  price={item.price}
                  isLiked={item.isLiked}
                  imageUrl={item.imageUrl}
                  rating={item.rating}
                  sales={item.sales}
                  badges={item.badges}
                  isNew={item.isNew}
                  discount={item.discount}
                />
                <div className="absolute top-2 right-2 z-20 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    className="bg-[#1F1A27]/80 hover:bg-[#1F1A27] p-1.5 rounded-full text-white"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveItem(item.id);
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
                <div className="bg-[#1F1A27] p-2 mt-2 text-xs rounded">
                  <p className="text-[var(--color-muted)]">Added on: {formatDate(item.addedOn)}</p>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="w-16 h-16 rounded-full bg-[#1F1A27] flex items-center justify-center mb-4">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg" className="text-[#7F56D9]">
              <path d="M12 21.35L10.55 20.03C5.4 15.36 2 12.28 2 8.5C2 5.42 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.09C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.42 22 8.5C22 12.28 18.6 15.36 13.45 20.04L12 21.35Z" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h2 className="text-xl font-medium mb-2">Your wishlist is empty</h2>
          <p className="text-center text-[var(--color-muted)] mb-6 max-w-md">
            Browse the marketplace and click the heart icon to add items to your wishlist.
          </p>
          <Link to="/" className="btn-primary py-3 px-6">
            Browse Marketplace
          </Link>
        </div>
      )}
    </div>
  );
};

export default WishlistPage;
